/**
 * Component: behaves as standard html select. Can have placeholder which will be added as the first element of the values.
 *
 * @attr {String} [class-modifier = ''] - additional css class that would be added to the select
 * @attr {String} [default-value = ''] - initial value
 * @attr {String} [no-empty-line] - remove first line if placeholder is empty
 * @attr {String} [placeholder] - placeholder
 * @attr {String} [disabled] - disabled
 * @attr {Array} [options = []] - list: { value, label }
 * @attr {Boolean} [filled] - define whenever some value is selected
 * @attr {Boolean} [sync-select-value] - define whenever the value of the native browser select should be synchronized with
 * the selectedValue of the NativeSelect component. In some cases (for example in ColorVariantSelector) those values
 * are not synchronized and user sees the wrong selected value on the frontend.
 *
 * @prop {String} [selectedValue = ''] - current selected value
 * @prop {Boolean} [focused] - focused
 *
 * <e2-contact-us-select
 *      .options="[{value: 'val1', label: 'Label 1'}, {value: 'val2', label: 'Label 2'}]"
 *      .options="[{value: 'optionValue1', label: 'description for first option'}, {value: 'optionValue2', label: 'description for second option'}]"
 *      default-value="optionValue1"
 *      @select="onSelectHandlerFunction"
 *      placeholder="Placeholder for select"
 *      no-empty-line
 * ></e2-contact-us-select>
 */

import { html } from 'lit-element';
import { classMap } from 'lit-html/directives/class-map';
import moment from 'moment';
import 'moment/locale/tr';

import { SimpleLitElement } from '@elab/components';

export class ContactUsSelect extends SimpleLitElement {
    static get properties() {
        return {
            classModifier: { type: String, attribute: 'class-modifier' },
            defaultValue: { type: String, attribute: 'default-value' },
            noEmptyLine: { type: Boolean, attribute: 'no-empty-line' },
            syncSelectValue: { type: Boolean, attribute: 'sync-select-value' },
            placeholder: { type: String, attribute: 'place-holder' },
            errorLabel: { type: String, attribute: 'error-label' },
            options: { type: Array },
            disabled: { type: Boolean },
            hasErrors: { type: Boolean, attribute: 'has-errors' },
            disabledPlaceholderOption: {
                type: Boolean,
                attribute: 'disabled-placeholder-option',
            },
            selectedValue: { type: String, attribute: false },
            focused: { type: Boolean, reflect: true },
            filled: { type: Boolean, reflect: true },
            required: { type: Boolean, attribute: 'required' },
            gtmLabel: {
                type: String,
                attribute: 'gtm-label',
            },
        };
    }

    constructor() {
        super();

        this.classModifier = '';
        this.placeholder = '';
        this.options = [];
        this.disabled = false;
        this.focused = false;
        this.filled = false;
        this.hasErrors = false;
        this.selectedValue = this.defaultValue = '';
        this.syncSelectValue = false;
        this.gtmLabel = '';
        this.disabledPlaceholderOption = false;
        this.required = true;
        this.errorLabel = '';
        this.noEmptyLine = false;
    }

    updated(_changedProperties) {
        super.updated(_changedProperties);
        if (_changedProperties.has('defaultValue')) {
            this.setSelectedValue(this.defaultValue);
        }
        if (_changedProperties.has('options')) {
            if (this.isResetSelectedValue(_changedProperties.get('options'), this.options)) {
                this.resetSelectedValue();
            }
        }
        if (_changedProperties.has('selectedValue')) {
            this.filled = !!this.selectedValue;

            if (this.syncSelectValue) {
                const selectNode = this.querySelector('select');
                if (selectNode.value !== this.selectedValue) {
                    selectNode.value = this.selectedValue;
                }
            }
        }
    }

    resetSelectedValue() {
        let value = '';

        if (this.defaultValue) {
            value = this.defaultValue;
        } else if (this.noEmptyLine && !this.placeholder) {
            value = this.options[0].value;
        }

        this.setSelectedValue(value);
    }

    setSelectedValue(value) {
        this.selectedValue = value;
    }

    isResetSelectedValue = (array1 = [], array2 = []) => {
        return array1.length && array2.length && JSON.stringify(array1) !== JSON.stringify(array2);
    };

    setFocus(value = false) {
        this.focused = value;
    }

    onSelect = e => {
        e.stopPropagation();
        this.selectedValue = e.target.value;
        this.dispatchEvent(new CustomEvent('select', { detail: this.selectedValue }));
    };

    onOptionClick = e => {
        e.stopPropagation();
        this.setFocus(false);
    };

    formatBasedOnLang = date =>
        moment(date)
            .locale(document.documentElement.lang)
            .format('ll');

    getOptionTemplate = ({ code, placed, disabled }) => {
        let placeValue = this.formatBasedOnLang(placed);
        placeValue = placeValue === 'Invalid date' ? '' : placeValue;
        const optionWithDate = code ? `${code} - ${placeValue}` : '';
        return html`
            <option
                @click="${e => !disabled && this.onOptionClick(e)}"
                value="${optionWithDate}"
                ?selected="${String(optionWithDate) === this.selectedValue}"
                ?disabled="${disabled}"
            >
                <div class="contact-us-order-list-item">
                    ${code
                        ? html`
                              ${code} - ${placeValue}
                          `
                        : this.label}
                </div>
            </option>
        `;
    };

    getEmptyOption = () => {
        this.label = this.placeholder ? `${this.placeholder} *` : ' *';
        return this.noEmptyLine && !this.placeholder
            ? null
            : this.getOptionTemplate({
                  code: '',
                  placed: '',
                  disabled: this.disabledPlaceholderOption,
              });
    };

    errorStyle = () =>
        classMap({
            'contact-us-error': true,
            'd-none': !this.hasErrors,
        });

    render() {
        return html`
            <select
                class="select ${this.classModifier} ${this.hasErrors
                    ? 'contact-us-error-select'
                    : ''}${this.focused ? 'select--focused' : ''}"
                @change="${this.onSelect}"
                @click="${() => this.setFocus(!this.focused)}"
                @blur="${() => this.setFocus(false)}"
                ?disabled="${this.disabled}"
                ?has-errors="${this.hasErrors}"
            >
                ${this.getEmptyOption()} ${this.options.map(this.getOptionTemplate)}
            </select>
            <i class="icon icon-arrow-down"></i>
            <span class="select select--error-label ${this.errorStyle()}">${this.errorLabel}</span>
        `;
    }
}

export default () => customElements.define('e2-contact-us-select', ContactUsSelect);
